import {
  DEFAULT_CATALOGUE_DESIGN,
  type CatalogueDesignKey,
} from '@eshop/designs';
import {
  Component,
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { lazyWithRetry } from '@tools/utils/lazyWithRetry.ts';

import { templateRegistry } from './system/TemplateRegistry';
import type { VisualTemplate } from './system/templateTypes';

interface TemplateBridgeProps {
  renderTemplate: (template: VisualTemplate) => ReactNode;
}

type TemplateBridgeComponent = ComponentType<TemplateBridgeProps>;

const VisualTemplateContext = createContext<VisualTemplate | null>(null);

export const useVisualTemplate = () => {
  const ctx = useContext(VisualTemplateContext);
  if (!ctx) {
    throw new Error('VisualTemplateContext no está disponible');
  }
  return ctx;
};

const createLazyTemplateComponent = (
  designKey: CatalogueDesignKey,
  fallbackKey: CatalogueDesignKey,
) =>
  lazyWithRetry(() =>
    templateRegistry.resolveTemplate(designKey, fallbackKey).then((template) => {
      const Bridge: TemplateBridgeComponent = ({ renderTemplate }) => <>{renderTemplate(template)}</>;
      Bridge.displayName = `TemplateBridge(${template.key})`;
      return { default: Bridge };
    }),
  );

interface TemplateErrorBoundaryProps {
  designKey: CatalogueDesignKey;
  onError: (error: Error) => void;
  onReset?: () => void;
  children: ReactNode;
}

interface TemplateErrorBoundaryState {
  error: Error | null;
}

class TemplateErrorBoundary extends Component<
  TemplateErrorBoundaryProps,
  TemplateErrorBoundaryState
> {
  state: TemplateErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): TemplateErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error): void {
    this.props.onError(error);
  }

  componentDidUpdate(prevProps: TemplateErrorBoundaryProps): void {
    if (prevProps.designKey !== this.props.designKey && this.state.error) {
      this.setState({ error: null });
      this.props.onReset?.();
    }
  }

  render() {
    if (this.state.error) {
      return null;
    }

    return this.props.children;
  }
}

export interface TemplateRendererProps {
  design: CatalogueDesignKey;
  fallbackDesign?: CatalogueDesignKey;
  loadingFallback?: ReactNode;
  errorFallback?: ReactNode | ((error: Error) => ReactNode);
  onError?: (error: Error) => void;
  transformTemplate?: (template: VisualTemplate) => VisualTemplate;
  render?: (template: VisualTemplate) => ReactNode;
  children?: ReactNode | ((template: VisualTemplate) => ReactNode);
  onResolvedDesign?: (design: CatalogueDesignKey, meta: { usedFallback: boolean }) => void;
}

export const TemplateRenderer = ({
  design,
  fallbackDesign = DEFAULT_CATALOGUE_DESIGN,
  loadingFallback = null,
  errorFallback,
  onError,
  transformTemplate,
  render,
  children,
  onResolvedDesign,
}: TemplateRendererProps) => {
  const [useFallbackDesign, setUseFallbackDesign] = useState(false);
  const [terminalError, setTerminalError] = useState<Error | null>(null);

  useEffect(() => {
    setUseFallbackDesign(false);
    setTerminalError(null);
  }, [design, fallbackDesign]);

  const activeDesign = useFallbackDesign ? fallbackDesign : design;

  useEffect(() => {
    onResolvedDesign?.(activeDesign, { usedFallback: useFallbackDesign });
  }, [activeDesign, onResolvedDesign, useFallbackDesign]);

  const TemplateBridge = useMemo(
    () => createLazyTemplateComponent(activeDesign, fallbackDesign),
    [activeDesign, fallbackDesign],
  );

  const applyTransform = useCallback(
    (template: VisualTemplate): VisualTemplate =>
      transformTemplate ? transformTemplate(template) : template,
    [transformTemplate],
  );

  const renderWithContext = useCallback(
    (template: VisualTemplate) => {
      const transformed = applyTransform(template);
      let content: ReactNode;
      if (render) {
        content = render(transformed);
      } else if (typeof children === 'function') {
        content = children(transformed);
      } else {
        content = children ?? null;
      }

      return (
        <VisualTemplateContext.Provider value={transformed}>{content}</VisualTemplateContext.Provider>
      );
    },
    [applyTransform, children, render],
  );

  const handleBoundaryError = useCallback(
    (error: Error) => {
      onError?.(error);
      if (!useFallbackDesign && design !== fallbackDesign) {
        setUseFallbackDesign(true);
        return;
      }

      setTerminalError(error);
    },
    [onError, useFallbackDesign, design, fallbackDesign],
  );

  const handleBoundaryReset = useCallback(() => {
    setTerminalError(null);
  }, []);

  if (terminalError) {
    if (typeof errorFallback === 'function') {
      return <>{errorFallback(terminalError)}</>;
    }

    return <>{errorFallback ?? null}</>;
  }

  return (
    <TemplateErrorBoundary
      designKey={activeDesign}
      onError={handleBoundaryError}
      onReset={handleBoundaryReset}
    >
      <Suspense fallback={loadingFallback}>
        <TemplateBridge renderTemplate={renderWithContext} />
      </Suspense>
    </TemplateErrorBoundary>
  );
};

export { VisualTemplateContext };
